import { GameMap, routeBullets } from "../map";
import { GameClient, runDisplay, podOriginName, type Msg, type QType } from "../net";
import { STATIONS, STATION_BY_ID } from "../stations";
import { roster, type Roster, photoUrl } from "../api";
import { ALL_CURSES, curseById, curseImage, drawText, resolveText, type CurseClear } from "../curses";
import { deckCardById, deckImage, cardFace } from "../deck";
import { openDice } from "./dice";
import { mountInfo } from "./info";
import { mountSheet } from "./sheet";
import { mountMeasureTab } from "./measureTab";
import { mountGameMenu } from "./gameMenu";
import { openPhotoSend } from "./photoSend";
import { LAYER_GROUPS, type Tile } from "./seeker";
import { esc } from "./util";

// Mobile-native MTA-themed Hider view. Same full-bleed shell as the seeker
// (floating top bar + gesture bottom sheet) with hider tabs: Timer · Questions
// · Hide · Measure. Reuses the proven engines (run state, messaging, assist,
// the locked ¼-mile zone on the map). See design-handoff/ for the spec.

type Tab = "timer" | "questions" | "cards" | "curses" | "hide" | "measure" | "overlays";
const TABS: { id: Tab; icon: string; label: string }[] = [
  { id: "timer", icon: "⏱", label: "Timer" },
  { id: "questions", icon: "?", label: "Questions" },
  { id: "cards", icon: "🃏", label: "Cards" },
  { id: "curses", icon: "💀", label: "Curses" },
  { id: "hide", icon: "◉", label: "Hide" },
  { id: "measure", icon: "📐", label: "Measure" },
  { id: "overlays", icon: "▦", label: "Overlays" },
];
// Derive a question's type for the card-draw instruction (mirrors answerChips).
function qTypeOf(m: Msg | undefined): QType | undefined {
  if (!m) return undefined;
  const cat = (m.category || "").toLowerCase();
  return m.question?.type
    ?? (cat.includes("radar") ? "radar" : cat.includes("thermo") ? "thermometer" : cat.includes("measur") ? "measuring" : cat.includes("match") ? "matching" : cat.includes("photo") ? "photo" : undefined);
}
// Reference overlays the hider can toggle (districts / airports / parks / POIs) so
// they can answer "same district as me?", "further from an airport than me?", etc.
// Skip the seeker-board layers (mask / zones / dots / elimination).
const HIDER_GROUPS = LAYER_GROUPS.filter((g) => g.name !== "Board");

// Several stations share a name (e.g. two "Spring St") — disambiguate those by
// appending their routes so the hider can't lock the wrong one.
const NAME_COUNTS: Record<string, number> = STATIONS.reduce((m, s) => { m[s.name] = (m[s.name] || 0) + 1; return m; }, {} as Record<string, number>);
const stationLabel = (s: { name: string; routes: string[] }) => NAME_COUNTS[s.name] > 1 ? `${s.name} (${s.routes.join("/")})` : s.name;

const PHASE: Record<string, { label: string; color: string; sub: string }> = {
  idle: { label: "HEAD START READY", color: "#8C7E64", sub: "Lock your spot, then start your run." },
  headstart: { label: "HEAD START", color: "#FF6319", sub: "Seekers begin when this hits 0:00." },
  running: { label: "HIDING", color: "#00933C", sub: "Run clock is banking your score." },
  paused: { label: "PAUSED", color: "#8C7E64", sub: "Clock held. Resume when ready." },
  stopped: { label: "CAUGHT", color: "#EE352E", sub: "Time banked. Add card minutes below." },
};

export function mountHider(root: HTMLElement, client: GameClient, opts: { onExit?: () => void } = {}) {
  document.body.classList.add("sk-active");
  root.innerHTML = `
    <div class="sk">
      <div id="map" class="sk-map"></div>

      <div class="sk-top">
        <button class="nav-btn" id="game-menu-btn" aria-label="Open menu"><span></span><span></span><span></span></button>
        <div class="sk-top-r">
          <div class="sk-fabs">
            <button class="sk-fab sk-fab-dice" id="sk-dice" aria-label="Roll dice">🎲</button>
            <button class="sk-fab sk-fab-loc" id="sk-locate" aria-label="Locate me">◎</button>
          </div>
          <div class="sk-runchip" id="sk-runchip" hidden><span class="sk-runchip-dot"></span><span id="sk-runchip-clk">--:--</span></div>
        </div>
      </div>

      <div class="sk-sheet" id="sk-sheet">
        <div class="sk-grab" id="sk-grab"><span></span><button class="sk-sheet-toggle" id="sk-sheet-full" aria-label="Full screen" title="Full screen">⛶</button><button class="sk-sheet-toggle" id="sk-sheet-toggle" aria-label="Hide panel" title="Hide panel">▾</button></div>
        <div class="sk-brand">
          <span class="sk-dots"><i style="background:#EE352E"></i><i style="background:#FCCC0A"></i><i style="background:#00933C"></i></span>
          <span class="sk-wordmark">HIDER · PRIVATE</span>
          <span class="sk-dots"><i style="background:#0039A6"></i><i style="background:#FF6319"></i><i style="background:#B933AD"></i></span>
        </div>
        <div class="sk-tabs" id="sk-tabs">
          ${TABS.map((t) => `<button class="sk-tab" data-tab="${t.id}"><span class="sk-tab-ic">${t.icon}</span>${t.label}</button>`).join("")}
        </div>
        <div class="sk-body jl-scroll" id="sk-body">
          <section data-panel="timer" id="h-timer"></section>
          <section data-panel="questions" id="h-questions" hidden></section>
          <section data-panel="cards" id="h-cards" hidden></section>
          <section data-panel="curses" id="h-curses" hidden></section>
          <section data-panel="hide" id="h-hide" hidden></section>
          <section data-panel="measure" id="h-measure" hidden>
            <div class="sk-mode-seg" id="sk-mmodes">
              <button data-mmode="measure" class="on">📐 Measure</button>
              <button data-mmode="close">📍 How close am I?</button>
            </div>
            <div id="sk-measure-basic"></div>
            <div id="sk-assist" hidden></div>
            <p class="sk-note" id="sk-measure-note" hidden>Ask the gamemaster to switch on <b>Matching assist mode</b> to compare distances to POIs, borders &amp; coastline.</p>
          </section>
          <section data-panel="overlays" id="h-overlays" hidden></section>
        </div>
      </div>
    </div>`;

  const gameMap = new GameMap(root.querySelector<HTMLElement>("#map")!);
  const fitMap = () => gameMap.map.resize();
  window.addEventListener("resize", fitMap);
  window.visualViewport?.addEventListener("resize", fitMap);
  requestAnimationFrame(fitMap);
  setTimeout(fitMap, 350);

  mountInfo(client);
  root.querySelector("#sk-locate")!.addEventListener("click", () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => gameMap.map.flyTo({ center: [p.coords.longitude, p.coords.latitude], zoom: 15 }),
      () => alert("Couldn't get your location."));
  });
  root.querySelector("#sk-dice")?.addEventListener("click", () => openDice());

  let names: Roster | null = null;
  roster().then((r) => { names = r; if (tab === "questions") renderQuestions(); }).catch(() => {});
  // Pod hiders have no teamId — their run/score is keyed by the round's pod.
  const hideKey = () => (client.mode === "podrotation" ? `pod${client.pod?.roundIdx ?? 0}` : client.mode === "group" ? "__hiders__" : client.identity.teamId || "");
  // The hider's own run: pod hiders read it from runsAll (client.run only tracks
  // a team's run, which a pod hider doesn't have).
  const hiderRun = () => (client.mode === "podrotation" ? (client.runsAll[hideKey()] ?? null) : client.run);

  function choose(id: string) {
    if (client.isOffLimits(id)) { alert("That station is off-limits or off the island."); return; }
    client.setHider(id, client.hider?.stationId === id ? !!client.hider?.confirmed : false);
    gameMap.select(id); gameMap.flyToStation(id); gameMap.showStationPopup(id);
  }
  gameMap.onStationClick = choose;

  // ---- Timer tab ---------------------------------------------------------
  function renderTimer() {
    const d = runDisplay(hiderRun(), client.config.headStartSec);
    const p = PHASE[d.phase];
    const bonus = client.scoresAll[hideKey()]?.cardBonusMin ?? 0;
    const btn = (act: string, label: string, cls: string) => `<button class="primary ${cls}" data-run="${act}">${label}</button>`;
    const ctrls =
      d.phase === "idle" ? btn("start", "Start run", "green")
      : d.phase === "stopped" ? btn("reset", "Reset run", "plain")
      : d.phase === "paused" ? btn("resume", "Resume", "green") + btn("stop", "Stop (caught)", "commit")
      : btn("pause", "Pause", "plain") + btn("stop", "Stop (caught)", "commit");
    const el = root.querySelector<HTMLElement>("#h-timer")!;
    el.innerHTML = `
      <div class="h-clockwrap">
        <span class="sk-phasebadge" style="background:${p.color}">${p.label}</span>
        <div class="sk-bigclock">${d.text}</div>
        <div class="sk-phasesub">${p.sub}</div>
      </div>
      <div class="sk-runctrls">${ctrls}</div>
      <div class="sk-seclabel" style="margin-top:16px">TIME BONUS</div>
      <div class="h-card">
        <div class="h-card-cap">Auto-totaled from the time-bonus cards in your hand${client.mode === "podrotation" ? ` — best ${client.deckCfg.maxTimeCards}` : ""}. Manage them in the <b>🃏 Cards</b> tab.</div>
        <div class="h-bonus-inline">+${bonus} min</div>
      </div>
      <p class="sk-note">The map shows your locked ¼-mile zone. Switch to <b>Measure</b> any time to check your cover. Use the ☰ menu to switch view or leave.</p>`;
    el.querySelectorAll<HTMLButtonElement>("[data-run]").forEach((b) => b.addEventListener("click", () => {
      const op = b.dataset.run!;
      if (op === "start" && !confirm("Start your run? The head-start countdown begins now.")) return;
      if (op === "reset" && !confirm("Reset your run clock?")) return;
      if (op === "stop") { if (!confirm("Stop the clock? This banks your final time.")) return; }
      client.runOp(op);
    }));
  }

  // ---- Questions tab -----------------------------------------------------
  // A squad's CURRENT members, reflecting the GM's live swing-move overrides
  // (client.pod.swingSquad), not just the static locked schedule — so the names
  // the hider sees match where answers actually route (worker squadOf).
  function squadMembers(j: number): string[] {
    const p = client.pod; if (!p?.schedule) return [];
    const round = p.roundIdx;
    const roundSquads = p.schedule.rounds[round] ?? [];
    const hidingPod = p.schedule.pods[round] ?? [];
    const effKey = (id: string) => p.swingSquad?.[id] || (() => { const idx = roundSquads.findIndex((s) => s.includes(id)); return idx >= 0 ? `r${round}q${idx}` : ""; })();
    return p.schedule.pods.flat().filter((id) => !hidingPod.includes(id) && effKey(id) === `r${round}q${j}`);
  }
  function teamName(tid: string): string {
    if (tid === "__seekers__") return "Seekers";
    const sq = /^r\d+q(\d+)$/.exec(tid); // pod squad key → "Squad A"
    if (sq) { const who = squadMembers(+sq[1]).map((id) => client.pod?.names?.[id] || id.slice(0, 5)).join(", "); return `Squad ${String.fromCharCode(65 + +sq[1])}${who ? ` (${who})` : ""}`; }
    return names?.teams.find((t) => t.id === tid)?.name ?? tid.slice(0, 6);
  }
  // Answer chips keyed to the structured question TYPE (matching→same/different,
  // measuring→closer/further, thermo→hotter/colder, radar→within/outside). Falls
  // back to the legacy `category` string for any pre-redesign messages.
  function answerChips(thread: import("../net").Msg[]): { chips: { label: string; answer: string }[]; replyTo?: string } {
    const last = [...thread].reverse().find((m) => !m.fromHider);
    const cat = (last?.category || "").toLowerCase();
    const type = last?.question?.type
      ?? (cat.includes("radar") ? "radar" : cat.includes("thermo") ? "thermometer" : cat.includes("measur") ? "measuring" : cat.includes("match") ? "matching" : undefined);
    const replyTo = last?.question?.id;
    if (type === "photo") return { chips: [], replyTo }; // answered with the 📷 button, not a chip
    if (type === "radar") return { chips: [{ label: "Yes — within", answer: "yes" }, { label: "No — outside", answer: "no" }], replyTo };
    if (type === "thermometer") return { chips: [{ label: "🔥 Hotter", answer: "hotter" }, { label: "❄️ Colder", answer: "colder" }], replyTo };
    if (type === "measuring") return { chips: [{ label: "Closer", answer: "closer" }, { label: "Further", answer: "further" }], replyTo };
    if (type === "matching") return { chips: [{ label: "Yes — same", answer: "yes" }, { label: "No — different", answer: "no" }], replyTo };
    return { chips: [{ label: "Yes", answer: "yes" }, { label: "No", answer: "no" }], replyTo };
  }
  function renderQuestions() {
    const el = root.querySelector<HTMLElement>("#h-questions")!;
    const tids = Object.keys(client.threads);
    const unanswered = tids.filter((t) => { const th = client.threads[t]; return th.length && !th[th.length - 1].fromHider; }).length;
    const photoBtn = `<button class="primary photo-open" id="h-photo">📷 Send a photo to a team</button>`;
    if (!tids.length) {
      el.innerHTML = `${photoBtn}<div class="sk-qhead"><span class="sk-seclabel">INCOMING QUESTIONS</span></div><div class="sk-empty">No questions from any team yet.</div>`;
      el.querySelector("#h-photo")?.addEventListener("click", () => openPhotoSend(client));
      return;
    }
    el.innerHTML = `
      ${photoBtn}
      <div class="sk-qhead">
        <span class="sk-seclabel">INCOMING QUESTIONS</span>
        <span class="sk-qbadge ${unanswered ? "hot" : ""}">${unanswered} to answer</span>
      </div>
      ${tids.map((tid) => {
        const thread = client.threads[tid] || [];
        const lastMsg = thread[thread.length - 1];
        const isVetoed = !!lastMsg && !lastMsg.fromHider && !!lastMsg.vetoed;
        const isDeleted = !!lastMsg && !lastMsg.fromHider && !!lastMsg.deleted;
        const isRandomized = !!lastMsg && !lastMsg.fromHider && !!lastMsg.randomized;
        const pending = !!lastMsg && !lastMsg.fromHider && !lastMsg.vetoed && !lastMsg.deleted && !lastMsg.randomized;
        const { chips, replyTo } = answerChips(thread);
        // qid (when pending) lets a free-text reply ALSO mark the question answered
        // so the seeker is unblocked — the same as tapping "Mark answered".
        const inputRow = (qid?: string) => `<div class="sk-msg-input"><input data-reply="${tid}" placeholder="Type an answer…" /><button data-send="${tid}"${qid ? ` data-qid="${esc(qid)}"` : ""}>Send</button><button class="qphoto" data-qphoto="${tid}"${replyTo ? ` data-replyto="${esc(replyTo)}"` : ""} title="Reply with a photo">📷</button></div>`;
        return `<div class="sk-qcard">
          <div class="sk-qcard-h"><span class="sk-qcard-dot"></span><span class="sk-qcard-name">${esc(teamName(tid))}</span></div>
          <div class="sk-thread compact">
            ${thread.map((m) => `<div class="sk-msg ${m.fromHider ? "me" : "them"} ${m.vetoed || m.deleted || m.randomized ? "vetoed" : ""}"><span class="sk-msg-cat">${esc(m.fromName)}${m.category ? " · " + esc(m.category) : ""}</span>${m.photoKey ? `<img class="msg-photo" src="${photoUrl(m.photoKey)}" loading="lazy" alt="photo"/>` : ""}${esc(m.text)}${m.vetoed ? `<span class="h-veto-tag">🚫 VETOED</span>` : ""}${m.deleted ? `<span class="h-veto-tag">🗑 DELETED</span>` : ""}${m.randomized ? `<span class="h-veto-tag">🎲 RANDOMIZED</span>` : ""}</div>`).join("")}
          </div>
          ${pending
            ? `<div class="h-draw-banner">🃏 Answer to draw cards — <b>${esc(drawText(qTypeOf(lastMsg)))}</b>. Or veto (no answer, no draw).</div>
               <div class="sk-qchips">${chips.map((c) => `<button class="sk-qchip" data-chip="${tid}" data-text="${esc(c.label)}" data-answer="${esc(c.answer)}"${replyTo ? ` data-replyto="${esc(replyTo)}"` : ""}>${esc(c.label)}</button>`).join("")}${client.hasVetoCard(tid) ? `<button class="sk-qchip veto" data-veto="${esc(lastMsg.id)}" data-thread="${tid}">🚫 Veto (1 card)</button>` : ""}</div>
               ${inputRow(replyTo)}
               ${replyTo ? `<button class="gm-set-wide" data-close="${tid}" data-qid="${esc(replyTo)}" style="margin-top:6px">✓ Mark answered — unlock their next question</button>` : ""}`
            : isVetoed
            ? `<div class="sk-answered vetoed">🚫 Vetoed — no answer given, no card draw.</div>`
            : isDeleted
            ? `<div class="sk-answered" style="color:var(--muted)">🗑 The seeker deleted this question.</div>`
            : isRandomized
            ? `<div class="sk-answered" style="color:var(--blue)">🎲 Randomized — answer a random same-category question of your choice.</div>`
            : `<div class="sk-answered">✓ Answered · reply again to revise</div>${inputRow()}`}
        </div>`;
      }).join("")}`;
    el.querySelectorAll<HTMLButtonElement>("[data-veto]").forEach((b) => b.addEventListener("click", () => {
      if (confirm("Veto this question?\n\nOnly veto if you have a veto card in your deck — each veto card can be used only once. The question gets no answer and earns no card draw.")) {
        client.vetoQuestion(b.dataset.thread!, b.dataset.veto!);
      }
    }));
    el.querySelector("#h-photo")?.addEventListener("click", () => openPhotoSend(client));
    el.querySelectorAll<HTMLButtonElement>("[data-qphoto]").forEach((b) => b.addEventListener("click", () => openPhotoSend(client, { target: b.dataset.qphoto, replyTo: b.dataset.replyto || undefined, replyingTo: teamName(b.dataset.qphoto!) })));
    el.querySelectorAll<HTMLButtonElement>("[data-chip]").forEach((b) => b.addEventListener("click", () => client.postMessage(b.dataset.chip!, b.dataset.text!, undefined, { answer: b.dataset.answer, replyTo: b.dataset.replyto })));
    el.querySelectorAll<HTMLButtonElement>("[data-send]").forEach((b) => b.addEventListener("click", () => {
      const inp = el.querySelector<HTMLInputElement>(`[data-reply="${b.dataset.send}"]`)!;
      if (inp.value.trim()) {
        client.postMessage(b.dataset.send!, inp.value.trim());
        // A typed reply to an open question also marks it answered (unblocks the seeker).
        if (b.dataset.qid) client.closeQuestion(b.dataset.send!, b.dataset.qid);
        inp.value = "";
      }
    }));
    el.querySelectorAll<HTMLButtonElement>("[data-close]").forEach((b) => b.addEventListener("click", () => client.closeQuestion(b.dataset.close!, b.dataset.qid!)));
  }

  // ---- Curses tab --------------------------------------------------------
  function clearLabel(clear: CurseClear): string {
    switch (clear.mode) {
      case "photo": return "Clears: they send a photo → you verify";
      case "notify": return "Clears: they tap “done”";
      case "hider": return "Clears: you lift it";
      case "timer": return `Clears: after ${clear.min[client.gameSize]} min`;
      case "none": return "Lasts the round";
    }
  }
  function renderCurses() {
    const el = root.querySelector<HTMLElement>("#h-curses")!;
    const size = client.gameSize;
    const inPlay = Object.values(client.curses).flat().filter((c) => c.status !== "cleared").sort((a, b) => b.ts - a.ts);
    el.innerHTML = `
      <div class="sk-seclabel">CURSE REFERENCE</div>
      <p class="sk-note" style="margin:0 0 8px">Curses are shuffled into your deck. You can only cast one you've drawn — play it from your <b>🃏 Cards</b> tab.</p>
      <div class="h-curse-list">
        ${ALL_CURSES.map((c) => `<div class="h-curse-opt">
          <img class="h-curse-thumb" src="${curseImage(c.id)}" alt="" loading="lazy" />
          <span class="h-curse-meta"><b>${c.emoji} ${esc(c.name)}</b><span class="h-curse-desc">${esc(resolveText(c.description, c, size))}</span><span class="h-curse-clear">${esc(clearLabel(c.clear))}</span></span>
        </div>`).join("")}
      </div>
      ${inPlay.length ? `<div class="sk-seclabel" style="margin-top:16px">CURSES YOU'VE CAST — VERIFY / LIFT</div>
      ${inPlay.map((c) => {
        const def = curseById(c.curseId);
        return `<div class="h-curse-active">
          <div class="h-curse-active-h"><b>${def?.emoji || "💀"} ${esc(def?.name || c.curseId)}</b><span class="h-curse-on">on ${esc(teamName(c.targetKey))}</span></div>
          ${c.status === "pending-verify"
            ? `<div class="h-curse-pending">📸 Photo submitted — verify to lift.${c.clearPhotoKey ? `<img class="msg-photo" src="${photoUrl(c.clearPhotoKey)}" loading="lazy" alt="clear photo"/>` : ""}</div>`
            : `<div class="sk-phasesub">${esc(resolveText(def?.description || "", def, size))}</div>`}
          <button class="primary green h-lift" data-target="${esc(c.targetKey)}" data-inst="${esc(c.instanceId)}">${c.status === "pending-verify" ? "✓ Verify & lift" : "Lift curse"}</button>
        </div>`;
      }).join("")}` : ""}`;
    el.querySelectorAll<HTMLButtonElement>(".h-lift").forEach((b) => b.addEventListener("click", () => client.liftCurse(b.dataset.target!, b.dataset.inst!)));
  }

  // ---- Cards tab (hand management) ---------------------------------------
  // Opponents the hider holds a hand against (the seeker squads/teams).
  function opponents(): { key: string; label: string }[] {
    if (client.photoTargets.length) return client.photoTargets.map((t) => ({ key: t.key, label: t.label }));
    return Object.keys(client.hands).map((k) => ({ key: k, label: teamName(k) }));
  }
  function renderCards() {
    const el = root.querySelector<HTMLElement>("#h-cards")!;
    const size = client.gameSize;
    const bonus = client.scoresAll[hideKey()]?.cardBonusMin ?? 0;
    el.innerHTML = `
      <div class="sk-seclabel">YOUR TIME BONUS</div>
      <div class="h-bonus-big">+${bonus}<small>min</small><span>auto-totaled from time cards${client.mode === "podrotation" ? ` · best ${client.deckCfg.maxTimeCards}` : ""}</span></div>
      ${opponents().map((opp) => {
        const hand = client.hand(opp.key);
        const limit = client.handLimit(opp.key);
        const over = hand.cards.length > limit;
        return `<div class="h-hand">
          <div class="sk-qcard-h"><span class="sk-qcard-name">🃏 ${esc(opp.label)}</span><span class="h-hand-count ${over ? "over" : ""}">${hand.cards.length}/${limit}</span></div>
          ${over ? `<div class="h-hand-over">Over the hand limit — discard ${hand.cards.length - limit}.</div>` : ""}
          ${hand.cards.length ? hand.cards.map((card) => cardRow(opp.key, card, size)).join("") : `<div class="sk-empty">No cards yet — answer this team's questions, or draw one below.</div>`}
          <button class="gm-set-wide h-draw" data-opp="${esc(opp.key)}" style="margin-top:8px">＋ Draw a card from ${esc(opp.label)}'s deck</button>
        </div>`;
      }).join("")}`;
    el.querySelectorAll<HTMLButtonElement>(".h-draw").forEach((b) => b.addEventListener("click", () => client.drawCard(b.dataset.opp!)));
    el.querySelectorAll<HTMLButtonElement>(".h-discard").forEach((b) => b.addEventListener("click", () => { if (confirm("Discard this card?")) client.discardCard(b.dataset.opp!, b.dataset.inst!); }));
    el.querySelectorAll<HTMLButtonElement>(".h-play").forEach((b) => b.addEventListener("click", () => {
      const oppKey = b.dataset.opp!, instId = b.dataset.inst!, cardId = b.dataset.card!;
      if (b.dataset.sel) { startPick(oppKey, instId, cardId); return; }
      const f = cardFace(cardId, client.gameSize);
      const prompt = f?.isCurse ? `Cast “${f.name}” on ${teamName(oppKey)}? It curses them and burns the card.` : `Play “${f?.name}”? This messages the team and burns the card.`;
      if (confirm(prompt)) client.playCard(oppKey, instId);
    }));
    el.querySelectorAll<HTMLImageElement>(".h-card-img").forEach((img) => img.addEventListener("click", () => openCardZoom(img.dataset.zoom!)));
  }
  // Tap a hand card to view it enlarged; ✕ or tap-outside to close.
  function openCardZoom(cardId: string) {
    document.querySelector("#h-card-zoom")?.remove();
    const f = cardFace(cardId, client.gameSize);
    const el = document.createElement("div");
    el.id = "h-card-zoom"; el.className = "h-modal";
    el.innerHTML = `<div class="h-zoom-box"><button class="h-zoom-x" aria-label="Close">✕</button><img class="h-zoom-img" src="${f?.image || deckImage(cardId)}" alt="${esc(f?.name || "")}" /></div>`;
    el.addEventListener("click", (e) => { const t = e.target as HTMLElement; if (t === el || t.closest(".h-zoom-x")) el.remove(); });
    document.body.appendChild(el);
  }
  function cardRow(oppKey: string, card: import("../net").HandCard, size: import("../curses").GameSize): string {
    const f = cardFace(card.cardId, size);
    const def = deckCardById(card.cardId);
    const playable = !!f?.isPowerup && def?.kind !== "veto"; // veto via Questions; time not playable; curses ARE playable
    const needSel = def?.kind === "duplicate" || def?.kind === "discard1draw2" || def?.kind === "discard2draw3";
    return `<div class="h-card-row">
      <img class="h-card-img" src="${f?.image || deckImage(card.cardId)}" alt="${esc(f?.name || "")}" data-zoom="${esc(card.cardId)}" title="Tap to enlarge" />
      <div class="h-card-meta">
        <b>${f?.isCurse ? "💀 " : ""}${esc(f?.name || card.cardId)}</b>
        <span class="h-card-desc">${esc(f?.description || "")}</span>
        <div class="h-card-acts">
          ${playable ? `<button class="primary green h-play" data-opp="${esc(oppKey)}" data-inst="${esc(card.instId)}" data-card="${esc(card.cardId)}" data-sel="${needSel ? "1" : ""}">${f?.isCurse ? "Cast" : "Play"}</button>` : ""}
          <button class="ghost h-discard" data-opp="${esc(oppKey)}" data-inst="${esc(card.instId)}">Discard</button>
        </div>
      </div>
    </div>`;
  }
  // Duplicate / Discard-N-Draw-M need the hider to pick target/discard cards first.
  let pickMode: { oppKey: string; instId: string; cardId: string; need: number; chosen: string[] } | null = null;
  function startPick(oppKey: string, instId: string, cardId: string) {
    const def = deckCardById(cardId);
    pickMode = { oppKey, instId, cardId, need: def?.kind === "discard2draw3" ? 2 : 1, chosen: [] };
    renderPickModal();
  }
  function renderPickModal() {
    let m = document.querySelector<HTMLElement>("#h-pick-modal");
    if (!pickMode) { m?.remove(); return; }
    const { oppKey, instId, cardId, need, chosen } = pickMode;
    const def = deckCardById(cardId);
    const isDup = def?.kind === "duplicate";
    const cards = client.hand(oppKey).cards.filter((c) => c.instId !== instId);
    if (!m) { m = document.createElement("div"); m.id = "h-pick-modal"; m.className = "h-modal"; document.body.appendChild(m); }
    m.innerHTML = `<div class="h-modal-box">
      <div class="h-modal-title">${esc(def?.name || "")}</div>
      <div class="h-modal-sub">Pick ${need} card${need > 1 ? "s" : ""} to ${isDup ? "copy into your hand" : "discard"}.</div>
      <div class="h-pick-grid">${cards.map((c) => { const f = cardFace(c.cardId, client.gameSize); const on = chosen.includes(c.instId); return `<button class="h-pickcard ${on ? "on" : ""}" data-pick="${esc(c.instId)}"><img src="${f?.image || deckImage(c.cardId)}" alt=""/><span>${esc(f?.name || c.cardId)}</span></button>`; }).join("") || `<div class="sk-empty">No other cards in hand.</div>`}</div>
      <div class="h-modal-acts"><button class="ghost" id="h-pick-cancel">Cancel</button><button class="primary green" id="h-pick-ok" ${chosen.length === need ? "" : "disabled"}>Play</button></div>
    </div>`;
    m.querySelectorAll<HTMLButtonElement>("[data-pick]").forEach((b) => b.addEventListener("click", () => {
      const id = b.dataset.pick!; const i = chosen.indexOf(id);
      if (i >= 0) chosen.splice(i, 1); else if (chosen.length < need) chosen.push(id);
      renderPickModal();
    }));
    m.querySelector("#h-pick-cancel")?.addEventListener("click", () => { pickMode = null; renderPickModal(); });
    m.querySelector("#h-pick-ok")?.addEventListener("click", () => {
      if (chosen.length !== need) return;
      if (isDup) client.playCard(oppKey, instId, { targetInstId: chosen[0] });
      else client.playCard(oppKey, instId, { discardIds: chosen });
      pickMode = null; renderPickModal();
    });
  }
  // The "draw N, keep K" prompt — pops over any tab when a draw is pending.
  // keep=1 → one tap keeps it; keep>1 (vetoed re-ask ×2) → multi-select then confirm.
  const drawKeep = new Set<string>(); // selected instIds for a keep-N draw
  function renderDrawModal() {
    const entries = Object.entries(client.pendingDraws);
    let m = document.querySelector<HTMLElement>("#h-draw-modal");
    if (!entries.length || pickMode) { if (!entries.length) { m?.remove(); drawKeep.clear(); } return; }
    const [oppKey, pd] = entries[0];
    const multi = pd.keep > 1;
    if (!m) { m = document.createElement("div"); m.id = "h-draw-modal"; m.className = "h-modal"; document.body.appendChild(m); }
    m.innerHTML = `<div class="h-modal-box">
      <div class="h-modal-title">Keep ${pd.keep} of ${pd.opts.length}</div>
      <div class="h-modal-sub">You drew these for answering <b>${esc(teamName(oppKey))}</b>. ${multi ? `Tap <b>${pd.keep}</b> to keep — the rest are discarded.` : "Tap one to keep — the rest are discarded."}</div>
      <div class="h-draw-grid">${pd.opts.map((o) => { const f = cardFace(o.cardId, client.gameSize); const on = drawKeep.has(o.instId); return `<button class="h-drawcard ${on ? "sel" : ""}" data-keep="${esc(o.instId)}" data-card="${esc(o.cardId)}"><img src="${f?.image || deckImage(o.cardId)}" alt="${esc(f?.name || "")}"/><span>${esc(f?.name || o.cardId)}</span></button>`; }).join("")}</div>
      ${multi ? `<div class="h-modal-acts"><button class="primary green" id="h-draw-ok" ${drawKeep.size === pd.keep ? "" : "disabled"}>Keep these ${drawKeep.size}/${pd.keep}</button></div>` : ""}
    </div>`;
    m.querySelectorAll<HTMLButtonElement>("[data-keep]").forEach((b) => b.addEventListener("click", () => {
      const id = b.dataset.keep!;
      if (multi) {
        if (drawKeep.has(id)) drawKeep.delete(id); else if (drawKeep.size < pd.keep) drawKeep.add(id);
        renderDrawModal();
      } else {
        const cf = cardFace(b.dataset.card!, client.gameSize);
        if (!confirm(`Keep “${cf?.name || "this card"}”? The other card${pd.opts.length > 2 ? "s" : ""} will be discarded.`)) return;
        client.keepDraw(oppKey, [id]);
        document.querySelectorAll("#h-draw-modal").forEach((x) => x.remove());
      }
    }));
    m.querySelector("#h-draw-ok")?.addEventListener("click", () => {
      if (drawKeep.size !== pd.keep) return;
      client.keepDraw(oppKey, [...drawKeep]);
      drawKeep.clear();
      document.querySelectorAll("#h-draw-modal").forEach((x) => x.remove());
    });
  }

  // ---- Hide tab ----------------------------------------------------------
  let search = "";
  function renderHide() {
    const el = root.querySelector<HTMLElement>("#h-hide")!;
    const id = client.hider?.stationId;
    const locked = !!client.hider?.confirmed;
    const chosen = id ? STATION_BY_ID[id] : null;
    const card = chosen
      ? `<div class="h-hide-card">
          <div class="h-hide-row">
            <div class="h-hide-badge" style="--c:${locked ? "#00933C" : "#C99A2E"}">${locked ? "🔒" : "📍"}</div>
            <div class="h-hide-info">
              <div class="h-hide-state">${locked ? "LOCKED IN" : "NOT LOCKED"}</div>
              <div class="h-hide-name">${esc(stationLabel(chosen))}</div>
              <div class="sk-routes">${routeBullets(chosen.routes)}</div>
            </div>
          </div>
          <button class="primary ${locked ? "green" : ""}" id="h-lock">${locked ? "✓ Locked in — tap to unlock" : "Lock in this spot"}</button>
        </div>`
      : `<div class="sk-empty">Tap a station on the map or list to set your ¼-mile zone.</div>`;
    const q = search.trim().toLowerCase();
    const rows = STATIONS
      .filter((s) => !q || s.name.toLowerCase().includes(q) || s.routes.join(" ").toLowerCase().includes(q))
      .map((s) => {
        const off = client.isOffLimits(s.id);
        const sel = client.hider?.stationId === s.id;
        const tag = off ? `<span class="h-tag off">OFF-LIMITS</span>` : sel ? `<span class="h-tag chosen">CHOSEN</span>` : "";
        return `<div class="h-strow ${off ? "off" : ""} ${sel ? "sel" : ""}" data-id="${s.id}">
          <span class="h-strow-name">${esc(stationLabel(s))}</span><span class="sk-routes">${routeBullets(s.routes)}</span>${tag}</div>`;
      }).join("");
    const podOrigin = podOriginName(client.pod?.origin);
    el.innerHTML = `${client.pod ? `<div class="sk-card pod-banner" style="margin-bottom:11px"><div class="sk-seclabel">YOUR POD IS HIDING (Round ${client.pod.roundIdx + 1})</div><div class="sk-phasesub">You start at <b>${esc(podOrigin)}</b>. Use the head start to travel <b>anywhere in NYC you can reach</b>, then lock the station you'll hide near — seekers have to find your hiding zone around it.</div></div>` : ""}
      ${card}
      <input class="sk-search" id="h-search" type="search" placeholder="Find your station…" value="${esc(search)}" />
      <div class="h-stlist">${rows}</div>`;
    el.querySelector("#h-lock")?.addEventListener("click", () => { if (id) client.setHider(id, !locked); });
    const si = el.querySelector<HTMLInputElement>("#h-search")!;
    si.addEventListener("input", () => { search = si.value; const list = el.querySelector<HTMLElement>(".h-stlist")!; list.innerHTML = listRows(); wireRows(list); });
    function listRows() {
      const qq = search.trim().toLowerCase();
      return STATIONS.filter((s) => !qq || s.name.toLowerCase().includes(qq) || s.routes.join(" ").toLowerCase().includes(qq))
        .map((s) => { const off = client.isOffLimits(s.id); const sel = client.hider?.stationId === s.id;
          const tag = off ? `<span class="h-tag off">OFF-LIMITS</span>` : sel ? `<span class="h-tag chosen">CHOSEN</span>` : "";
          return `<div class="h-strow ${off ? "off" : ""} ${sel ? "sel" : ""}" data-id="${s.id}"><span class="h-strow-name">${esc(stationLabel(s))}</span><span class="sk-routes">${routeBullets(s.routes)}</span>${tag}</div>`; }).join("");
    }
    function wireRows(container: HTMLElement) {
      container.querySelectorAll<HTMLElement>(".h-strow").forEach((r) => r.addEventListener("click", () => choose(r.dataset.id!)));
    }
    wireRows(el.querySelector<HTMLElement>(".h-stlist")!);
  }

  // ---- bottom sheet (detents + collapse toggle) -------------------------
  const sheet = mountSheet(root, fitMap);

  // ---- Measure tab (mirrors the seeker's) --------------------------------
  const measure = mountMeasureTab(root, client, gameMap, "Drag the A &amp; B handles — straight-line distance from your spot.", (on) => sheet.collapse(on));
  mountGameMenu(root, client, { onExit: opts.onExit });

  // ---- Overlays tab: toggle reference layers (districts, airports, parks, POIs)
  const layerOn = new Map<string, boolean>([["airports", true]]); // airports show on the map by default
  function applyTile(t: Tile, on: boolean) {
    layerOn.set(t.id, on);
    t.layers?.forEach((id) => gameMap.setLayerVisible(id, on));
  }
  function renderOverlays() {
    const el = root.querySelector<HTMLElement>("#h-overlays")!;
    const total = [...layerOn.values()].filter(Boolean).length;
    el.innerHTML = `
      <div class="sk-layers-head"><span class="sk-seclabel">MAP OVERLAYS</span><span class="sk-on-count">${total} on</span></div>
      <p class="sk-note" style="margin:0 0 8px">Turn on districts, airports or places to answer questions like “are you in the same borough as me?” — then use <b>Measure</b> to check a distance.</p>
      ${HIDER_GROUPS.map((g) => `<div class="sk-group"><div class="sk-group-name">${g.name}</div>
        <div class="sk-tiles">${g.tiles.map((t) => { const on = !!layerOn.get(t.id); return `<button class="sk-tile ${on ? "on" : ""}" data-tile="${t.id}" style="--c:${t.color}"><span class="sk-tile-dot">${t.emoji}</span><span class="sk-tile-lbl">${esc(t.label)}</span></button>`; }).join("")}</div></div>`).join("")}`;
    el.querySelectorAll<HTMLButtonElement>("[data-tile]").forEach((b) => b.addEventListener("click", () => {
      const t = HIDER_GROUPS.flatMap((g) => g.tiles).find((x) => x.id === b.dataset.tile)!;
      applyTile(t, !layerOn.get(t.id));
      renderOverlays();
    }));
  }

  // ---- tabs --------------------------------------------------------------
  let tab: Tab = "timer";
  function setTab(t: Tab) {
    const prev = tab;
    tab = t;
    root.querySelectorAll<HTMLElement>("[data-panel]").forEach((p) => { p.hidden = p.dataset.panel !== t; });
    root.querySelectorAll<HTMLButtonElement>(".sk-tab").forEach((b) => b.classList.toggle("on", b.dataset.tab === t));
    if (prev === "measure" && t !== "measure") measure.leave();
    if (sheet.name === "peek") sheet.set("half");
    if (t === "timer") renderTimer();
    if (t === "questions") renderQuestions();
    if (t === "cards") renderCards();
    if (t === "curses") renderCurses();
    if (t === "hide") renderHide();
    if (t === "measure") measure.enter();
    if (t === "overlays") renderOverlays();
  }
  root.querySelectorAll<HTMLButtonElement>(".sk-tab").forEach((b) => b.addEventListener("click", () => setTab(b.dataset.tab as Tab)));

  // ---- top bar + live updates -------------------------------------------
  function renderTop() {
    const d = runDisplay(hiderRun(), client.config.headStartSec);
    const chip = root.querySelector<HTMLElement>("#sk-runchip")!;
    chip.hidden = d.phase === "idle";
    root.querySelector<HTMLElement>("#sk-runchip-clk")!.textContent = d.text;
    chip.dataset.phase = d.phase;
  }

  client.subscribe(() => {
    gameMap.refresh(client);
    if (client.hider?.stationId) gameMap.select(client.hider.stationId);
    renderTop();
    renderDrawModal();   // the keep-1 prompt pops over any tab
    if (tab === "timer") renderTimer();
    if (tab === "questions") renderQuestions();
    if (tab === "cards") renderCards();
    if (tab === "curses") renderCurses();
    if (tab === "hide") renderHide();
  });
  setInterval(() => { renderTop(); if (tab === "timer") renderTimer(); }, 1000);

  setTab("timer");
}
